import React, { useEffect, useMemo, useState } from 'react';
import {
  Mail,
  Bell,
  CalendarHeart,
  Image as ImageIcon,
  Sparkles,
  Loader2,
  Check,
  Send,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';

import {
  EmailPreferences as PrefsT,
  getEmailPrefs,
  setEmailPrefs,
  subscribeEmailPrefs,
  DEFAULT_EMAIL_PREFS,
} from '@/lib/emailPrefs';
import { sendWeeklyDigest } from '@/lib/emailNotifications';
import { supabase } from '@/lib/supabase';


/**
 * Mirror the user's "family activity" preference into the server-readable
 * email_prefs table so the send-family-notification edge function can
 * honor opt-outs at delivery time. Best-effort — failures are silent.
 */
const syncFamilyActivityPref = async (
  userId: string | null | undefined,
  enabled: boolean,
) => {
  if (!userId) return;
  try {
    await supabase.from('email_prefs').upsert(
      {
        user_id: userId,
        family_activity_emails: enabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
  } catch (err) {
    console.debug('[EmailPreferences] email_prefs sync (ignored)', err);
  }
};

interface Props {
  userId: string | null | undefined;
  userEmail?: string | null;
  userName?: string | null;
}

interface ChannelDef {
  key: keyof Omit<PrefsT, 'all_email_off' | 'updated_at'>;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const CHANNELS: ChannelDef[] = [
  {
    key: 'important',
    title: 'Important updates',
    description:
      'When a relative joins through your invite, or when someone claims a profile in your family tree.',
    icon: Bell,
  },
  {
    key: 'weekly_digest',
    title: 'Weekly digest',
    description:
      'A warm Sunday recap of new memories, new family members, and birthdays coming up — “Your KinsCircle family update.”',
    icon: Sparkles,
  },
  {
    key: 'birthdays',
    title: 'Birthday reminders',
    description:
      'A gentle heads-up a few days before someone in your family has a birthday.',
    icon: CalendarHeart,
  },
  {
    key: 'memory_activity',
    title: 'Memory activity',
    description:
      'When a relative uploads a new photo, video, or memory — or comments on or reacts to one.',
    icon: ImageIcon,
  },
];

const formatUpdated = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
};

const EmailPreferences: React.FC<Props> = ({ userId, userEmail, userName }) => {
  const [prefs, setPrefs] = useState<PrefsT>(() => getEmailPrefs(userId));
  const [sendingSample, setSendingSample] = useState(false);

  // Re-hydrate when the user id resolves (e.g. after auth load).
  useEffect(() => {
    setPrefs(getEmailPrefs(userId));
    const unsub = subscribeEmailPrefs(userId, (next) => setPrefs(next));
    return unsub;
  }, [userId]);

  const updatedLabel = useMemo(
    () => formatUpdated(prefs.updated_at),
    [prefs.updated_at]
  );

  const handleToggleChannel = (key: ChannelDef['key'], checked: boolean) => {
    const next = setEmailPrefs(userId, { [key]: checked } as Partial<PrefsT>);
    setPrefs(next);
    toast.success(
      checked
        ? `${labelFor(key)} emails turned on`
        : `${labelFor(key)} emails turned off`
    );
  };

  const handleMasterToggle = (checked: boolean) => {
    // checked === true means "emails on", so all_email_off === !checked
    const next = setEmailPrefs(userId, { all_email_off: !checked });
    setPrefs(next);
    toast.success(
      checked
        ? 'Family emails turned on. We’ll keep things warm and useful.'
        : 'All family emails turned off. You can switch them back on anytime.'
    );
  };

  const handleResetDefaults = () => {
    const next = setEmailPrefs(userId, { ...DEFAULT_EMAIL_PREFS });
    setPrefs(next);
    toast.success('Email preferences reset to defaults.');
  };

  const handleSendSampleDigest = async () => {
    if (!userEmail && !userId) {
      toast.error('We don’t have an email on file to send to.');
      return;
    }
    if (prefs.all_email_off || !prefs.weekly_digest) {
      toast.error(
        'Turn the weekly digest on first, then send yourself a sample.'
      );
      return;
    }
    setSendingSample(true);
    try {
      const res = await sendWeeklyDigest({
        recipientUserId: userId ?? null,
        recipientEmail: userEmail ?? null,
        recipientName: userName ?? null,
        summary: {
          newMemoriesCount: 3,
          newMembersCount: 1,
          newCommentsCount: 4,
          upcomingBirthdays: [
            { name: 'Aunt Rose', daysUntil: 5 },
            { name: 'Cousin Theo', daysUntil: 9 },
          ],
          suggestedAction: 'add_memory',
          recentMemoryTitles: [
            'Sunday lunch at Grandma’s',
            'Theo’s first bike ride',
            'Throwback: 1998 reunion',
          ],
        },
      });
      if (res.ok) {
        toast.success('Sample digest sent — check your inbox.');
      } else if (res.skipped) {
        toast.error(
          res.reason === 'channel_disabled'
            ? 'Weekly digest is turned off for your account.'
            : 'No email on file to send to.'
        );
      } else {
        // Edge function might not be deployed yet — be honest but warm.
        toast(
          'Saved your preferences. The sample digest will arrive once delivery is fully configured.',
          { description: 'No action needed from you.' }
        );
      }
    } finally {
      setSendingSample(false);
    }
  };

  const allOn = !prefs.all_email_off;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-fuchsia-600" />
          Email notifications
        </CardTitle>
        <CardDescription>
          Choose what KinsCircle emails you. We keep them warm, family-focused,
          and free of clutter — never marketing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Master switch */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-fuchsia-50/60 to-pink-50/40 p-4 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white border border-fuchsia-200 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-fuchsia-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">
                Family emails
              </div>
              <p className="text-sm text-slate-600 leading-snug">
                Master switch. When this is off, no KinsCircle emails are sent
                to you — even if individual settings below are on.
              </p>
              {userEmail && (
                <p className="text-xs text-slate-500 mt-1">
                  Sending to{' '}
                  <span className="font-mono text-slate-700">{userEmail}</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`text-xs font-medium ${
                allOn ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              {allOn ? 'On' : 'Off'}
            </span>
            <Switch
              checked={allOn}
              onCheckedChange={handleMasterToggle}
              aria-label="Toggle all KinsCircle family emails"
            />
          </div>
        </div>

        <Separator />

        {/* Per-channel toggles */}
        <div className={`space-y-3 ${allOn ? '' : 'opacity-60'}`}>
          {CHANNELS.map((ch) => {
            const Icon = ch.icon;
            const enabled = Boolean(prefs[ch.key]);
            const inputId = `email-pref-${ch.key}`;
            return (
              <div
                key={ch.key}
                className="rounded-lg border border-slate-200 p-4 flex items-start justify-between gap-4"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-fuchsia-50 border border-fuchsia-100 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-fuchsia-600" />
                  </div>
                  <div className="min-w-0">
                    <Label
                      htmlFor={inputId}
                      className="font-semibold text-slate-900 cursor-pointer"
                    >
                      {ch.title}
                    </Label>
                    <p className="text-sm text-slate-600 leading-snug">
                      {ch.description}
                    </p>
                  </div>
                </div>
                <Switch
                  id={inputId}
                  checked={enabled && allOn}
                  disabled={!allOn}
                  onCheckedChange={(v) => handleToggleChannel(ch.key, v)}
                  aria-label={`Toggle ${ch.title} emails`}
                />
              </div>
            );
          })}
        </div>

        <Separator />

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {updatedLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                Saved {updatedLabel}
              </span>
            ) : (
              <span>Defaults: weekly digest, important updates, birthday reminders, and memory activity are all on.</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetDefaults}
              className="rounded-full"
            >
              Reset defaults
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSendSampleDigest}
              disabled={sendingSample || !allOn || !prefs.weekly_digest}
              className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1.5"
            >
              {sendingSample ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send me a sample digest
            </Button>
          </div>
        </div>

        <p className="text-xs text-slate-500 leading-snug">
          We only ever include safe summaries in emails — never private full-resolution
          photos or videos. To open a memory, you’ll always be asked to sign in first.
        </p>
      </CardContent>
    </Card>
  );
};

const labelFor = (key: ChannelDef['key']): string => {
  const ch = CHANNELS.find((c) => c.key === key);
  return ch?.title ?? 'Email';
};

export default EmailPreferences;
